import { useSyncExternalStore } from 'react'

/**
 * One clock for the whole application.
 *
 * A table of fifty rows showing «5 минут назад» is fifty cells that have to
 * re-read the time, and fifty `setInterval`s is fifty timers the browser has to
 * schedule and fifty renders that do not coincide. This is a single tick every
 * thirty seconds that every subscriber shares, so the whole screen ages in one
 * pass — and it stops entirely when nothing is subscribed.
 *
 * Thirty seconds because the shortest unit anything displays is a minute: a
 * faster tick would re-render the panel for a string that did not change, and a
 * slower one would let «1 минуту назад» stand for two.
 */
const TICK_MS = 30_000

const listeners = new Set<() => void>()
let now = Date.now()
let timer: ReturnType<typeof setInterval> | undefined

const subscribe = (onChange: () => void) => {
  /*
   * Re-read on every arrival, not only on the tick. The cached value is
   * otherwise as old as the module — a row mounting ten minutes into a session
   * would be dated from when the tab opened, and it would stay wrong until the
   * next tick. React re-reads the snapshot after subscribing, so a value that
   * moved here reaches the component that just mounted.
   */
  now = Date.now()
  listeners.add(onChange)

  timer ??= setInterval(() => {
    now = Date.now()
    for (const listener of listeners) listener()
  }, TICK_MS)

  return () => {
    listeners.delete(onChange)
    if (listeners.size === 0 && timer !== undefined) {
      clearInterval(timer)
      timer = undefined
    }
  }
}

/** Milliseconds, refreshed on the shared tick. */
export function useNow(): number {
  return useSyncExternalStore(
    subscribe,
    () => now,
    // The server snapshot: irrelevant in an SPA, but `useSyncExternalStore`
    // wants one and returning the live value keeps it from ever mismatching.
    () => now,
  )
}
